import { Navigate, useParams } from 'react-router-dom'
import { JOB_PARAM, REPORT_PARAM, useJobOverlay } from '../lib/job-overlay.js'
import { JobPopup } from './job-card/JobPopup.js'
import { ReportFormPopup } from './report-form/ReportFormPopup.js'

/**
 * The job, and the report on it, drawn over whatever screen is underneath.
 *
 * Mounted once in `AppShell` rather than by the screens that open jobs, which
 * is the whole of decision 61: there is one way a job opens, it is the same
 * popup from the Jobs list, the board, a client, the inbox or a search result,
 * and the screen behind it keeps its scroll and its filters because it never
 * unmounted. Everything that used to link to a page at `/jobs/:id` now adds
 * `?jobId=` to the page it is already on.
 *
 * Two layers, closed one at a time. A report opens over the job card and Done
 * takes the person back to the card, not out to the list.
 *
 * In the field app the job itself is `FieldJob`, drawn by the field shell, and
 * this draws only what goes over it: the office's tabbed card when `?card=full`
 * asks for it, and the report. Decision 93.
 */
export function JobOverlay({ field = false }: { field?: boolean }) {
  const { jobId, jobServiceId, fullCard, closeJob, closeFullCard, closeReport } = useJobOverlay()

  return (
    <>
      {field ? (
        <JobPopup jobId={fullCard ? jobId : null} onClose={closeFullCard} />
      ) : (
        <JobPopup jobId={jobId} onClose={closeJob} />
      )}
      {jobId !== null && jobServiceId !== null ? (
        <ReportFormPopup jobId={jobId} jobServiceId={jobServiceId} onClose={closeReport} />
      ) : null}
    </>
  )
}

/**
 * The old addresses, kept working.
 *
 * `/jobs/:jobId` and `/jobs/:jobId/report/:jobServiceId` were real pages until
 * decision 61 and are in links people have already sent each other. They now
 * open the Jobs list with the popup over it, which is what clicking a job does
 * everywhere else — so an old link and a new one arrive at the same screen
 * rather than at two.
 */
export function OpenJobFromPath() {
  const { jobId = '', jobServiceId } = useParams()

  const search = new URLSearchParams({ [JOB_PARAM]: jobId })
  if (jobServiceId) search.set(REPORT_PARAM, jobServiceId)

  return <Navigate to={{ pathname: '/jobs', search: `?${search}` }} replace />
}
